import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { StatusMessage } from "../../components/ui/StatusMessage";
import { useAuth } from "../../hooks/useAuth";
import { clientService } from "../../services/clientService";
import type { ClientProfile } from "../../types/trainer";

type OnboardingForm = {
  preferred_name: string;
  phone: string;
  date_of_birth: string;
  timezone: string;
  primary_goal: string;
  goal_details: string;
  target_date: string;
  current_training_level: string;
  training_history: string;
  weekly_availability: string;
  gym_access: string;
  equipment_available: string;
  injuries_limitations: string;
  medical_considerations: string;
  dietary_requirements: string;
  allergies: string;
  food_preferences: string;
  medication_disclosure: string;
  supplement_disclosure: string;
  ped_choice: string;
  ped_details: string;
  lifestyle_notes: string;
  sleep_stress_notes: string;
  work_schedule_constraints: string;
  progress_photo_front_path: string;
  progress_photo_side_path: string;
  progress_photo_back_path: string;
  consent_sensitive_data: boolean;
  consent_photos: boolean;
  consent_coaching_disclaimer: boolean;
};

const EMPTY_FORM: OnboardingForm = {
  preferred_name: "",
  phone: "",
  date_of_birth: "",
  timezone: "",
  primary_goal: "",
  goal_details: "",
  target_date: "",
  current_training_level: "",
  training_history: "",
  weekly_availability: "",
  gym_access: "",
  equipment_available: "",
  injuries_limitations: "",
  medical_considerations: "",
  dietary_requirements: "",
  allergies: "",
  food_preferences: "",
  medication_disclosure: "",
  supplement_disclosure: "",
  ped_choice: "",
  ped_details: "",
  lifestyle_notes: "",
  sleep_stress_notes: "",
  work_schedule_constraints: "",
  progress_photo_front_path: "",
  progress_photo_side_path: "",
  progress_photo_back_path: "",
  consent_sensitive_data: false,
  consent_photos: false,
  consent_coaching_disclaimer: false,
};

const STEPS = [
  "Basics",
  "Goals",
  "Training",
  "Health",
  "Nutrition",
  "Disclosure",
  "Lifestyle",
  "Photos",
  "Consent",
];

function nullable(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function TextArea({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  return (
    <label className="block text-sm font-medium text-ink">
      {label}
      {hint ? <span className="mt-1 block text-sm font-normal leading-6 text-graphite/65">{hint}</span> : null}
      <textarea
        className="mt-2 min-h-28 w-full rounded-md border border-line bg-field px-3 py-2 text-base text-ink outline-none transition focus:border-ink focus:ring-2 focus:ring-ink/10"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectField({
  label,
  options,
  value,
  onChange,
  required,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block text-sm font-medium text-ink">
      {label}
      <select
        className="mt-2 block min-h-11 w-full rounded-md border border-line bg-field px-3 py-2 text-base text-ink"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      >
        <option value="">Select...</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function CheckboxField({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex gap-3 rounded-md border border-line bg-field p-4 text-sm leading-6 text-ink">
      <input
        className="mt-1 h-4 w-4"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        type="checkbox"
      />
      <span>{label}</span>
    </label>
  );
}

export function ClientOnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState<ClientProfile | null>(null);
  const [form, setForm] = useState<OnboardingForm>(EMPTY_FORM);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState<"front" | "side" | "back" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadOnboarding() {
      if (!user) return;
      setLoading(true);
      setError(null);

      try {
        const currentClient = await clientService.getCurrentClient(user.id);
        if (!mounted) return;
        setClient(currentClient);

        if (currentClient) {
          const existing = await clientService.getOnboardingResponse(currentClient.id);
          if (!mounted || !existing) return;
          setForm({
            ...EMPTY_FORM,
            preferred_name: existing.preferred_name ?? "",
            phone: existing.phone ?? "",
            date_of_birth: existing.date_of_birth ?? "",
            timezone: existing.timezone ?? "",
            primary_goal: existing.primary_goal ?? "",
            goal_details: existing.goal_details ?? "",
            target_date: existing.target_date ?? "",
            current_training_level: existing.current_training_level ?? "",
            training_history: existing.training_history ?? "",
            weekly_availability: existing.weekly_availability ?? "",
            gym_access: existing.gym_access ?? "",
            equipment_available: existing.equipment_available ?? "",
            injuries_limitations: existing.injuries_limitations ?? "",
            medical_considerations: existing.medical_considerations ?? "",
            dietary_requirements: existing.dietary_requirements ?? "",
            allergies: existing.allergies ?? "",
            food_preferences: existing.food_preferences ?? "",
            medication_disclosure: existing.medication_disclosure ?? "",
            supplement_disclosure: existing.supplement_disclosure ?? "",
            ped_choice: existing.ped_disclosure ?? "",
            lifestyle_notes: existing.lifestyle_notes ?? "",
            sleep_stress_notes: existing.sleep_stress_notes ?? "",
            progress_photo_front_path: existing.progress_photo_front_path ?? "",
            progress_photo_side_path: existing.progress_photo_side_path ?? "",
            progress_photo_back_path: existing.progress_photo_back_path ?? "",
            consent_sensitive_data: existing.consent_sensitive_data,
            consent_photos: existing.consent_photos,
            consent_coaching_disclaimer: existing.consent_coaching_disclaimer,
            ped_details: "",
            work_schedule_constraints: "",
          });
        }
      } catch (caughtError) {
        console.error("Unable to load client onboarding", caughtError);
        if (mounted) setError("Unable to load onboarding. Please refresh and try again.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void loadOnboarding();

    return () => {
      mounted = false;
    };
  }, [user]);

  function updateField<K extends keyof OnboardingForm>(key: K, value: OnboardingForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function validateCurrentStep() {
    if (step === 1 && !form.primary_goal) {
      setError("Choose a primary goal before continuing.");
      return false;
    }

    setError(null);
    return true;
  }

  function nextStep() {
    if (!validateCurrentStep()) return;
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  }

  function previousStep() {
    setError(null);
    setStep((current) => Math.max(current - 1, 0));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client || !user) return;

    if (!form.primary_goal) {
      setError("Primary goal is required.");
      setStep(1);
      return;
    }

    if (!form.consent_sensitive_data || !form.consent_coaching_disclaimer) {
      setError("Please complete the required consent acknowledgements.");
      setStep(8);
      return;
    }

    if (
      (form.progress_photo_front_path || form.progress_photo_side_path || form.progress_photo_back_path)
      && !form.consent_photos
    ) {
      setError("Please confirm photo consent because you uploaded progress photos.");
      setStep(8);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await clientService.saveOnboardingResponse({
        tenant_id: client.tenant_id,
        client_id: client.id,
        user_id: user.id,
        status: "draft",
        preferred_name: nullable(form.preferred_name),
        phone: nullable(form.phone),
        date_of_birth: nullable(form.date_of_birth),
        timezone: nullable(form.timezone),
        primary_goal: form.primary_goal,
        goal_details: nullable(form.goal_details),
        target_date: nullable(form.target_date),
        current_training_level: nullable(form.current_training_level),
        training_history: nullable(form.training_history),
        weekly_availability: nullable(form.weekly_availability),
        gym_access: nullable(form.gym_access),
        equipment_available: nullable(form.equipment_available),
        injuries_limitations: nullable(form.injuries_limitations),
        medical_considerations: nullable(form.medical_considerations),
        dietary_requirements: nullable(form.dietary_requirements),
        allergies: nullable(form.allergies),
        food_preferences: nullable(form.food_preferences),
        medication_disclosure: nullable(form.medication_disclosure),
        supplement_disclosure: nullable(form.supplement_disclosure),
        ped_disclosure: nullable(
          [form.ped_choice, form.ped_details].filter(Boolean).join(" - "),
        ),
        lifestyle_notes: nullable(
          [form.lifestyle_notes, form.work_schedule_constraints].filter(Boolean).join("\n\nSchedule: "),
        ),
        sleep_stress_notes: nullable(form.sleep_stress_notes),
        progress_photo_front_path: nullable(form.progress_photo_front_path),
        progress_photo_side_path: nullable(form.progress_photo_side_path),
        progress_photo_back_path: nullable(form.progress_photo_back_path),
        consent_sensitive_data: form.consent_sensitive_data,
        consent_photos: form.consent_photos,
        consent_coaching_disclaimer: form.consent_coaching_disclaimer,
      });

      await clientService.submitOnboardingResponse(response.id);
      navigate("/client", { replace: true });
    } catch (caughtError) {
      console.error("Unable to submit client onboarding", caughtError);
      setError("Unable to submit onboarding. Please check the form and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function uploadPhoto(kind: "front" | "side" | "back", file: File | null) {
    if (!file || !client) return;

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Progress photos must be JPG, PNG or WebP files.");
      return;
    }

    setUploadingPhoto(kind);
    setError(null);

    try {
      const path = await clientService.uploadOnboardingPhoto({ client, kind, file });
      const field = `progress_photo_${kind}_path` as keyof Pick<
        OnboardingForm,
        "progress_photo_front_path" | "progress_photo_side_path" | "progress_photo_back_path"
      >;
      updateField(field, path);
    } catch (caughtError) {
      console.error("Unable to upload onboarding photo", caughtError);
      setError("Unable to upload that photo. Please try again or continue without photos.");
    } finally {
      setUploadingPhoto(null);
    }
  }

  function renderStep() {
    switch (step) {
      case 0:
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Preferred name" value={form.preferred_name} onChange={(event: ChangeEvent<HTMLInputElement>) => updateField("preferred_name", event.target.value)} />
            <Input label="Phone" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} />
            <Input label="Date of birth" type="date" value={form.date_of_birth} onChange={(event) => updateField("date_of_birth", event.target.value)} />
            <Input label="Timezone" value={form.timezone} onChange={(event) => updateField("timezone", event.target.value)} placeholder="Europe/London" />
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <SelectField label="Primary goal" required value={form.primary_goal} onChange={(value) => updateField("primary_goal", value)} options={["fat loss", "muscle gain", "strength", "fitness/conditioning", "health/confidence", "sport/event prep", "other"]} />
            <TextArea label="Goal details" value={form.goal_details} onChange={(value) => updateField("goal_details", value)} />
            <Input label="Target date" type="date" value={form.target_date} onChange={(event) => updateField("target_date", event.target.value)} />
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <SelectField label="Current training level" value={form.current_training_level} onChange={(value) => updateField("current_training_level", value)} options={["complete beginner", "returning after break", "consistent beginner", "intermediate", "advanced"]} />
            <TextArea label="Training history" value={form.training_history} onChange={(value) => updateField("training_history", value)} />
            <TextArea label="Weekly availability" value={form.weekly_availability} onChange={(value) => updateField("weekly_availability", value)} />
            <TextArea label="Gym access" value={form.gym_access} onChange={(value) => updateField("gym_access", value)} />
            <TextArea label="Equipment available" value={form.equipment_available} onChange={(value) => updateField("equipment_available", value)} />
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <TextArea label="Injuries or limitations" value={form.injuries_limitations} onChange={(value) => updateField("injuries_limitations", value)} />
            <TextArea label="Medical considerations" hint="Share anything your coach should consider. This is not medical advice." value={form.medical_considerations} onChange={(value) => updateField("medical_considerations", value)} />
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <TextArea label="Dietary requirements" value={form.dietary_requirements} onChange={(value) => updateField("dietary_requirements", value)} />
            <TextArea label="Allergies" value={form.allergies} onChange={(value) => updateField("allergies", value)} />
            <TextArea label="Food preferences or dislikes" value={form.food_preferences} onChange={(value) => updateField("food_preferences", value)} />
          </div>
        );
      case 5:
        return (
          <div className="space-y-4">
            <TextArea label="Medication disclosure" value={form.medication_disclosure} onChange={(value) => updateField("medication_disclosure", value)} />
            <TextArea label="Supplement disclosure" value={form.supplement_disclosure} onChange={(value) => updateField("supplement_disclosure", value)} />
            <SelectField label="Are you currently using, planning to use, or have recently used PEDs, anabolic steroids, hormone treatments, or similar performance-enhancing substances?" value={form.ped_choice} onChange={(value) => updateField("ped_choice", value)} options={["No", "Yes", "Prefer not to say", "I'd rather discuss this directly with my coach"]} />
            <TextArea label="Optional PED details" value={form.ped_details} onChange={(value) => updateField("ped_details", value)} />
          </div>
        );
      case 6:
        return (
          <div className="space-y-4">
            <TextArea label="Sleep and stress notes" value={form.sleep_stress_notes} onChange={(value) => updateField("sleep_stress_notes", value)} />
            <TextArea label="Lifestyle notes" value={form.lifestyle_notes} onChange={(value) => updateField("lifestyle_notes", value)} />
            <TextArea label="Work pattern or schedule constraints" value={form.work_schedule_constraints} onChange={(value) => updateField("work_schedule_constraints", value)} />
          </div>
        );
      case 7:
        return (
          <div className="space-y-4">
            <StatusMessage
              title="Progress photos are optional"
              message="Front, side and back photos can help your coach track progress. They are stored in a private Supabase Storage bucket and are not public."
            />
            {[
              ["front", "Front photo", form.progress_photo_front_path],
              ["side", "Side photo", form.progress_photo_side_path],
              ["back", "Back photo", form.progress_photo_back_path],
            ].map(([kind, label, path]) => (
              <label className="block rounded-md border border-line bg-field p-4 text-sm text-graphite/70" key={kind}>
                <span className="font-semibold text-ink">{label}</span>
                <input
                  accept="image/jpeg,image/png,image/webp"
                  className="mt-3 block w-full text-sm"
                  disabled={uploadingPhoto !== null}
                  onChange={(event) => {
                    void uploadPhoto(kind as "front" | "side" | "back", event.target.files?.[0] ?? null);
                    event.target.value = "";
                  }}
                  type="file"
                />
                {uploadingPhoto === kind ? <span className="mt-2 block">Uploading...</span> : null}
                {path ? <span className="mt-2 block text-pine">Uploaded securely.</span> : null}
              </label>
            ))}
          </div>
        );
      default:
        return (
          <div className="space-y-4">
            <StatusMessage
              title="Consent required"
              message="This onboarding contains sensitive health, lifestyle and body-progress information. Submit only when you are comfortable sharing it with your coach/team for coaching purposes."
            />
            <CheckboxField checked={form.consent_sensitive_data} onChange={(checked) => updateField("consent_sensitive_data", checked)} label="I consent to CoachGrid storing and sharing this onboarding information with my assigned coach/team for coaching purposes." />
            <CheckboxField checked={form.consent_photos} onChange={(checked) => updateField("consent_photos", checked)} label="I consent to progress photos being stored and used for coaching progress tracking once photo upload is enabled." />
            <CheckboxField checked={form.consent_coaching_disclaimer} onChange={(checked) => updateField("consent_coaching_disclaimer", checked)} label="I understand this is coaching support and not medical advice." />
          </div>
        );
    }
  }

  if (loading) return <FullPageLoader label="Loading onboarding" />;
  if (!client) {
    return (
      <EmptyState eyebrow="Client onboarding" title="No client profile found.">
        Ask your coach to confirm your invite has been accepted for the right email address.
      </EmptyState>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <p className="eyebrow">Client onboarding</p>
      <h1 className="mt-3 text-3xl font-semibold text-ink">Tell your coach what matters</h1>
      <p className="mt-3 max-w-3xl text-base leading-7 text-graphite/70">
        Complete this once after accepting your invite. Your answers are saved to your client profile and shared with your assigned coaching team.
      </p>

      <div className="mt-6 grid gap-2 sm:grid-cols-3 lg:grid-cols-9">
        {STEPS.map((label, index) => (
          <button
            className={[
              "rounded-md border px-3 py-2 text-xs font-semibold",
              index === step ? "border-ink bg-ink text-white" : "border-line bg-field text-graphite",
            ].join(" ")}
            key={label}
            onClick={() => setStep(index)}
            type="button"
          >
            {index + 1}. {label}
          </button>
        ))}
      </div>

      <form className="mt-6" onSubmit={submit}>
        <AdminPanel title={STEPS[step]}>
          <div className="space-y-5">
            {error ? <StatusMessage message={error} tone="error" /> : null}
            {renderStep()}
            <div className="flex flex-col gap-3 border-t border-line pt-5 sm:flex-row sm:justify-between">
              <Button disabled={step === 0 || submitting} onClick={previousStep} type="button" variant="secondary">
                Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={nextStep} type="button">
                  Next
                </Button>
              ) : (
                <Button isLoading={submitting} type="submit">
                  Submit onboarding
                </Button>
              )}
            </div>
          </div>
        </AdminPanel>
      </form>
    </div>
  );
}
